import React, { useEffect, useMemo, useRef, useState } from "react";
import {
  Box,
  Chip,
  CircularProgress,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  Divider,
  FormControl,
  Grid,
  IconButton,
  InputAdornment,
  InputLabel,
  ListSubheader,
  MenuItem,
  Select,
  Tab,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Tabs,
  TextField,
  Tooltip,
} from "@mui/material";
import SearchIcon from "@mui/icons-material/Search";
import { alpha } from "@mui/material/styles";
import AddCircleOutlineIcon from "@mui/icons-material/AddCircleOutline";
import RefreshOutlinedIcon from "@mui/icons-material/RefreshOutlined";
import EditOutlinedIcon from "@mui/icons-material/EditOutlined";
import DeleteOutlineIcon from "@mui/icons-material/DeleteOutline";
import CheckCircleOutlineIcon from "@mui/icons-material/CheckCircleOutline";
import AccessTimeOutlinedIcon from "@mui/icons-material/AccessTimeOutlined";
import PersonOutlineOutlinedIcon from "@mui/icons-material/PersonOutlineOutlined";
import ViewListOutlinedIcon from "@mui/icons-material/ViewListOutlined";
import ViewColumnOutlinedIcon from "@mui/icons-material/ViewColumnOutlined";
import AssignmentOutlinedIcon from "@mui/icons-material/AssignmentOutlined";
import CloseIcon from "@mui/icons-material/Close";
import SaveOutlinedIcon from "@mui/icons-material/SaveOutlined";
import PlayCircleOutlineIcon from "@mui/icons-material/PlayCircleOutline";
import DownloadOutlinedIcon from "@mui/icons-material/DownloadOutlined";
import AttachFileOutlinedIcon from "@mui/icons-material/AttachFileOutlined";
import CallOutlinedIcon from "@mui/icons-material/CallOutlined";
import VisibilityOutlinedIcon from "@mui/icons-material/VisibilityOutlined";
import HistoryOutlinedIcon from "@mui/icons-material/HistoryOutlined";
import MDBox from "components/MDBox";
import MDTypography from "components/MDTypography";
import MDButton from "components/MDButton";
import CTLNotification from "application/components/CTLNotification";
import { application } from "globals/endpoints";
import { useMaterialUIController } from "context";
import { latLng } from "leaflet";

// ─── API URLs ────────────────────────────────────────────────────────────────

const API_LIST = "task/list";
const API_LIST_BY_STATUS = (s) => `task/listWithStatus/${s}`;
const API_DELAYED_LIST = "taskDelay/list";
const API_CREATE = "task/create";
const API_UPDATE = "task/update";
const API_DELETE = "task/delete";
const API_PRIORITY_OPTIONS = "taskPriority/listAll";
const API_TYPE_OPTIONS = "taskType/listAll";
const API_USER_OPTIONS = "userRole/listAll/user";
const API_REVIEWER_OPTIONS = "userRole/listAll/taskManager";
const API_GROUP_OPTIONS = "userGroup/listAll/uniqueGroup";
const API_FILE_UPLOAD = "task/uploadFile";

// ─── Status filter config ────────────────────────────────────────────────────

const STATUS_FILTERS = [
  { label: "All Tasks", key: "", color: "default" },
  { label: "New", key: "new", apiKey: "new", color: "info" },
  { label: "In Progress", key: "inProgress", apiKey: "inProgress", color: "primary" },
  { label: "Pending", key: "pending", apiKey: "pending", color: "warning" },
  { label: "For Approval", key: "pendingForApproval", apiKey: "pendingForApproval", color: "secondary" },
  { label: "Completed", key: "completed", apiKey: "completed", color: "success" },
  { label: "Delayed", key: "delayed", apiKey: "delayed", color: "error" },
];

const BOARD_COLUMNS = [
  { label: "New", statusKey: "New", color: "info" },
  { label: "In Progress", statusKey: "In Progress", color: "primary" },
  { label: "Pending", statusKey: "Pending", color: "warning" },
  { label: "For Approval", statusKey: "Pending For Approval", color: "secondary" },
  { label: "Completed", statusKey: "Completed", color: "success" },
  { label: "Delayed", statusKey: "Delayed", color: "error" },
];

// ─── Priority → MUI color ────────────────────────────────────────────────────

const PRIORITY_COLOR_MAP = {
  critical: "error",
  high: "warning",
  medium: "info",
  low: "success",
};

function priorityColor(p) {
  if (!p) return "default";
  return PRIORITY_COLOR_MAP[p.toLowerCase()] || "default";
}

function statusColor(s) {
  const map = {
    "New": "info",
    "In Progress": "primary",
    "Pending": "warning",
    "Pending For Approval": "secondary",
    "Completed": "success",
    "Delayed": "error",
  };
  return map[s] || "default";
}

// light bg per status (used in list rows)
const STATUS_BG_COLOR = {
  "New": (t) => alpha(t.palette.info.main, 0.06),
  "In Progress": (t) => alpha(t.palette.primary.main, 0.06),
  "Pending": (t) => alpha(t.palette.warning.main, 0.06),
  "Pending For Approval": (t) => alpha(t.palette.secondary.main, 0.06),
  "Completed": (t) => alpha(t.palette.success.main, 0.06),
  "Delayed": (t) => alpha(t.palette.error.main, 0.06),
};

// ─── Helpers ─────────────────────────────────────────────────────────────────

function isOverdue(dateStr, currentStatus) {
  if (!dateStr || currentStatus === "Completed") return false;
  const d = new Date(dateStr.replace(" ", "T"));
  return !isNaN(d.getTime()) && d < new Date();
}

function fmtDate(dateStr) {
  if (!dateStr) return "—";
  const d = new Date(dateStr.replace(" ", "T"));
  if (isNaN(d.getTime())) return dateStr;
  return d.toLocaleDateString("en-IN", { day: "2-digit", month: "short", year: "2-digit" });
}

function isoToLocal(str) {
  if (!str) return "";
  return str.replace(" ", "T").slice(0, 16);
}

function localToApi(str) {
  if (!str) return "";
  return str.replace("T", " ").slice(0, 16);
}

function nowLocal() {
  const n = new Date();
  const pad = (v) => String(v).padStart(2, "0");
  return `${n.getFullYear()}-${pad(n.getMonth() + 1)}-${pad(n.getDate())}T${pad(n.getHours())}:${pad(n.getMinutes())}`;
}

function nowApiFormat() {
  const n = new Date();
  const pad = (v) => String(v).padStart(2, "0");
  return `${n.getFullYear()}-${pad(n.getMonth() + 1)}-${pad(n.getDate())} ${pad(n.getHours())}:${pad(n.getMinutes())}`;
}

// ─── Empty form state ─────────────────────────────────────────────────────────

const EMPTY_FORM = {
  id: 0,
  tasksName: "",
  name: "",
  contactNo: "",
  taskPriority: "",
  taskType: "",
  assignedTo: "",
  assignedUser: "",
  assignedGroup: "",
  reviewer: "",
  assignedOn: "",
  expectedCompletionDate: "",
  taskDescription: "",
  filePath: "",
  currentStats: "New",
};

const PAGE_ATTRIBUTES = { modes: [] };

// ─── TaskCard — single slim row ───────────────────────────────────────────────

function TaskCard({ task, onEdit, onDelete, onQuickComplete, onView, darkMode }) {
  const overdue = isOverdue(task.expectedCompletionDate, task.currentStats);
  const isCompleted = task.currentStats === "Completed";
  const pColor = priorityColor(task.taskPriority);
  const sColor = statusColor(task.currentStats);
  const dotColor = sColor;
  const statusBg = STATUS_BG_COLOR[task.currentStats] || null;

  return (
    <Box
      onClick={() => onView && onView(task)}
      sx={{
        display: "flex",
        alignItems: "center",
        gap: 1,
        px: 1,
        py: "6px",
        mb: 0.5,
        minHeight: 44,
        borderRadius: 1,
        cursor: "pointer",
        backgroundColor: statusBg || "transparent",
        border: (t) =>
          `1px solid ${alpha(
            overdue ? t.palette.error.main : t.palette.divider,
            overdue ? 0.45 : 0.5,
          )}`,
        "&:hover": {
          filter: "brightness(0.97)",
          borderColor: (t) => alpha(t.palette.info.main, 0.5),
          backgroundColor: (t) => alpha(t.palette.info.main, 0.04),
        },
        transition: "all 0.12s ease",
      }}
    >
      {/* Status dot */}
      <Box
        sx={{
          width: 7,
          height: 7,
          borderRadius: "50%",
          flexShrink: 0,
          backgroundColor: (t) => t.palette[dotColor]?.main || t.palette.grey[400],
        }}
      />

      {/* ID + Task name — flex-1, truncated */}
      <MDTypography
        sx={{
          flex: "1 1 0",
          minWidth: 0,
          fontSize: "0.68rem",
          fontWeight: 600,
          whiteSpace: "nowrap",
          overflow: "hidden",
          textOverflow: "ellipsis",
          opacity: isCompleted ? 0.65 : 1,
          // textDecoration: isCompleted ? "line-through" : "none",
        }}
      >
        <MDTypography
          component="span"
          sx={{ fontSize: "0.6rem", color: "text", fontWeight: 400, mr: 0.3 }}
        >
          {task.id} &#183;&nbsp;
        </MDTypography>
        {task.tasksName}
      </MDTypography>

      {/* Status chip — fixed 100px */}
      <Box sx={{ width: 100, flexShrink: 0, display: "flex", alignItems: "center" }}>
        <Chip
          label={task.currentStats}
          size="small"
          color={sColor}
          sx={(t) => ({
            height: 17,
            fontSize: "0.56rem",
            fontWeight: 700,
            maxWidth: "100%",
            "& .MuiChip-label": { px: 0.6 },
            ...(darkMode && sColor !== "default" && t.palette[sColor] && {
              backgroundColor: t.palette[sColor].main,
              color: "#fff",
            }),
          })}
        />
      </Box>

      {/* Priority chip — fixed 62px */}
      <Box sx={{ width: 62, flexShrink: 0, display: "flex", alignItems: "center" }}>
        {task.taskPriority && (
          <Chip
            label={task.taskPriority}
            size="small"
            color={pColor}
            variant="outlined"
            sx={(t) => ({
              height: 16,
              fontSize: "0.54rem",
              maxWidth: "100%",
              "& .MuiChip-label": { px: 0.5 },
              ...(darkMode && pColor !== "default" && t.palette[pColor] && {
                color: t.palette[pColor].main,
                borderColor: t.palette[pColor].main,
              }),
            })}
          />
        )}
      </Box>

      {/* Task type — fixed 68px */}
      <Box sx={{ width: 68, flexShrink: 0, display: "flex", alignItems: "center" }}>
        {task.taskType && (
          <Chip
            label={task.taskType}
            size="small"
            variant="outlined"
            sx={{
              height: 16,
              fontSize: "0.54rem",
              maxWidth: "100%",
              "& .MuiChip-label": { px: 0.5 },
            }}
          />
        )}
      </Box>

      {/* Assignee — fixed 84px */}
      <Box sx={{ width: 84, flexShrink: 0, display: "flex", alignItems: "center", gap: 0.2 }}>
        <PersonOutlineOutlinedIcon sx={{ fontSize: 10, color: "text", flexShrink: 0 }} />
        <MDTypography
          sx={{
            fontSize: "0.58rem",
            color: "text",
            whiteSpace: "nowrap",
            overflow: "hidden",
            textOverflow: "ellipsis",
          }}
        >
          {task.assignedUser || task.assignedGroup || "—"}
        </MDTypography>
      </Box>

      {/* Due date — fixed 76px */}
      <Box sx={{ width: 76, flexShrink: 0, display: "flex", alignItems: "center", gap: 0.2 }}>
        <AccessTimeOutlinedIcon
          sx={{ fontSize: 10, color: overdue ? "error.main" : "text", flexShrink: 0 }}
        />
        <MDTypography
          sx={{
            fontSize: "0.58rem",
            color: overdue ? "error.main" : "text",
            fontWeight: overdue ? 700 : 400,
            whiteSpace: "nowrap",
          }}
        >
          {fmtDate(task.expectedCompletionDate)}
        </MDTypography>
      </Box>

      {/* Divider before actions */}
      <Box sx={{ width: "1px", height: 18, backgroundColor: "divider", flexShrink: 0, mx: 0.25 }} />

      {/* Action buttons — fixed 90px */}
      <Box
        sx={{ width: 90, flexShrink: 0, display: "flex", gap: 0.3, alignItems: "center" }}
        onClick={(e) => e.stopPropagation()}
      >
        <Tooltip title="View Details">
          <IconButton
            size="small"
            sx={{ p: 0.25, color: "info.main" }}
            onClick={() => onView && onView(task)}
          >
            <VisibilityOutlinedIcon sx={{ fontSize: 14 }} />
          </IconButton>
        </Tooltip>
        {!isCompleted && (
          <Tooltip title="Mark Complete">
            <IconButton
              size="small"
              sx={{ p: 0.25, color: "success.main" }}
              onClick={() => onQuickComplete(task)}
            >
              <CheckCircleOutlineIcon sx={{ fontSize: 14 }} />
            </IconButton>
          </Tooltip>
        )}
        <Tooltip title={isCompleted ? "Cannot edit completed task" : "Edit"}>
          <span>
            <IconButton
              size="small"
              disabled={isCompleted}
              sx={{ p: 0.25, color: isCompleted ? "text" : "warning.main" }}
              onClick={() => !isCompleted && onEdit(task)}
            >
              <EditOutlinedIcon sx={{ fontSize: 14 }} />
            </IconButton>
          </span>
        </Tooltip>
        <Tooltip title="Delete">
          <IconButton
            size="small"
            sx={{ p: 0.25, color: "error.main" }}
            onClick={() => onDelete(task.id)}
          >
            <DeleteOutlineIcon sx={{ fontSize: 14 }} />
          </IconButton>
        </Tooltip>
      </Box>
    </Box>
  );
}

// ─── BoardCard — multi-row detail card for board view ────────────────────────

function BoardCard({ task, onEdit, onDelete, onQuickComplete, onView, darkMode }) {
  const overdue = isOverdue(task.expectedCompletionDate, task.currentStats);
  const isCompleted = task.currentStats === "Completed";
  const pColor = priorityColor(task.taskPriority);
  const sColor = statusColor(task.currentStats);

  return (
    <Box
      sx={{
        mb: 0.75,
        p: "7px 8px",
        borderRadius: 1.5,
        // backgroundColor: (t) => alpha(t.palette.background.paper, 0.9),
        border: (t) =>
          `1px solid ${alpha(
            overdue ? t.palette.error.main : t.palette.divider,
            overdue ? 0.5 : 0.45,
          )}`,
        borderLeft: (t) =>
          `3px solid ${t.palette[sColor]?.main || t.palette.grey[400]}`,
        "&:hover": { filter: "brightness(0.97)" },
        transition: "all 0.12s ease",
      }}
    >
      {/* Row 1: ID + Subject */}
      <MDTypography
        sx={{
          fontSize: "0.68rem",
          fontWeight: 700,
          lineHeight: 1.3,
          mb: 0.5,
          textDecoration: isCompleted ? "line-through" : "none",
          opacity: isCompleted ? 0.65 : 1,
          whiteSpace: "nowrap",
          overflow: "hidden",
          textOverflow: "ellipsis",
        }}
      >
        <MDTypography
          component="span"
          sx={{ fontSize: "0.58rem", color: "text", fontWeight: 400, mr: 0.4 }}
        >
          {task.id} &#183;&nbsp;
        </MDTypography>
        {task.tasksName}
      </MDTypography>

      {/* Row 2: Priority + Type chips */}
      <Box sx={{ display: "flex", gap: 0.5, flexWrap: "wrap", mb: 0.5 }}>
        {task.taskPriority && (
          <Chip
            label={task.taskPriority}
            size="small"
            color={pColor}
            variant="outlined"
            sx={(t) => ({
              height: 15,
              fontSize: "0.53rem",
              "& .MuiChip-label": { px: 0.45 },
              ...(darkMode && pColor !== "default" && t.palette[pColor] && {
                color: t.palette[pColor].main,
                borderColor: t.palette[pColor].main,
              }),
            })}
          />
        )}
        {task.taskType && (
          <Chip
            label={task.taskType}
            size="small"
            variant="outlined"
            sx={{ height: 15, fontSize: "0.53rem", "& .MuiChip-label": { px: 0.45 } }}
          />
        )}
      </Box>

      {/* Row 3: Customer */}
      {(task.name || task.contactNo) && (
        <Box sx={{ display: "flex", alignItems: "center", gap: 0.4, mb: 0.4 }}>
          <MDTypography sx={{ fontSize: "0.58rem", color: "text" }}>
            {[task.name, task.contactNo].filter(Boolean).join(" · ")}
          </MDTypography>
        </Box>
      )}

      {/* Row 4: Assignee */}
      <Box sx={{ display: "flex", alignItems: "center", gap: 0.3, mb: 0.4 }}>
        <PersonOutlineOutlinedIcon sx={{ fontSize: 10, color: "text" }} />
        <MDTypography sx={{ fontSize: "0.58rem", color: "text" }}>
          {task.assignedUser || task.assignedGroup || "—"}
        </MDTypography>
        {task.reviewer && (
          <MDTypography sx={{ fontSize: "0.55rem", color: "text" }}>
            · rev: {task.reviewer}
          </MDTypography>
        )}
      </Box>

      {/* Row 5: Dates */}
      <Box sx={{ display: "flex", alignItems: "center", gap: 0.3, mb: 0.5 }}>
        <AccessTimeOutlinedIcon
          sx={{ fontSize: 10, color: overdue ? "error.main" : "text" }}
        />
        <MDTypography
          sx={{
            fontSize: "0.58rem",
            color: overdue ? "error.main" : "text",
            fontWeight: overdue ? 700 : 400,
          }}
        >
          {fmtDate(task.assignedOn)} → {fmtDate(task.expectedCompletionDate)}
        </MDTypography>
      </Box>

      {/* Row 6: Actions */}
      <Box
        sx={{
          display: "flex",
          justifyContent: "flex-end",
          gap: 0.3,
          pt: 0.3,
          borderTop: (t) => `1px solid ${alpha(t.palette.divider, 0.35)}`,
        }}
      >
        <Tooltip title="View Details">
          <IconButton size="small" sx={{ p: 0.3, color: "info.main" }} onClick={() => onView && onView(task)}>
            <VisibilityOutlinedIcon sx={{ fontSize: 13 }} />
          </IconButton>
        </Tooltip>
        {!isCompleted && (
          <Tooltip title="Mark Complete">
            <IconButton size="small" sx={{ p: 0.3, color: "success.main" }} onClick={() => onQuickComplete(task)}>
              <CheckCircleOutlineIcon sx={{ fontSize: 13 }} />
            </IconButton>
          </Tooltip>
        )}
        <Tooltip title={isCompleted ? "Cannot edit completed task" : "Edit"}>
          <span>
            <IconButton
              size="small"
              disabled={isCompleted}
              sx={{ p: 0.3, color: isCompleted ? "text" : "warning.main" }}
              onClick={() => !isCompleted && onEdit(task)}
            >
              <EditOutlinedIcon sx={{ fontSize: 13 }} />
            </IconButton>
          </span>
        </Tooltip>
        <Tooltip title="Delete">
          <IconButton size="small" sx={{ p: 0.3, color: "error.main" }} onClick={() => onDelete(task.id)}>
            <DeleteOutlineIcon sx={{ fontSize: 13 }} />
          </IconButton>
        </Tooltip>
      </Box>
    </Box>
  );
}

// ─── BoardColumn ──────────────────────────────────────────────────────────────

function BoardColumn({ column, tasks, onEdit, onDelete, onQuickComplete, onView, darkMode }) {
  return (
    <Box
      sx={{
        display: "flex",
        flexDirection: "column",
        minWidth: 210,
        width: 210,
        flexShrink: 0,
        borderRadius: 1.5,
        border: (t) => `1px solid ${alpha(t.palette.divider, 0.55)}`,
        overflow: "hidden",
        backgroundColor: (t) => alpha(t.palette.background.default, 0.55),
      }}
    >
      {/* Column Header */}
      <Box
        sx={{
          px: 1,
          py: 0.6,
          display: "flex",
          alignItems: "center",
          gap: 0.5,
          backgroundColor: (t) =>
            alpha(t.palette[column.color]?.main || t.palette.info.main, 0.1),
          borderBottom:`1px solid ${darkMode ? alpha("#ffffff", 0.35) : alpha("#000000", 0.35)
            }`,
          flexShrink: 0,
        }}
      >
        <MDTypography
          sx={{
            fontWeight: 700,
            fontSize: "0.68rem",
            flex: 1,
            color: (t) => t.palette[column.color]?.main || t.palette.info.main,
          }}
        >
          {column.label}
        </MDTypography>
        <Chip
          label={tasks.length}
          size="small"
          color={column.color}
          sx={(t) => ({
            height: 15,
            fontSize: "0.58rem",
            "& .MuiChip-label": { px: 0.45 },
            ...(darkMode && column.color !== "default" && t.palette[column.color] && {
              backgroundColor: t.palette[column.color].main,
              color: "#fff",
            }),
          })}
        />
      </Box>

      {/* Tasks */}
      <Box
        sx={{
          flex: 1,
          overflowY: "auto",
          p: 0.6,
          scrollbarWidth: "thin",
          "&::-webkit-scrollbar": { width: 3 },
          "&::-webkit-scrollbar-thumb": { borderRadius: 3, backgroundColor: "rgba(0,0,0,0.15)" },
        }}
      >
        {tasks.length === 0 ? (
          <MDTypography
            sx={{
              display: "block",
              textAlign: "center",
              color: "text",
              fontSize: "0.6rem",
              py: 2,
            }}
          >
            Empty
          </MDTypography>
        ) : (
          tasks.map((t) => (
            <BoardCard
              key={t.id}
              task={t}
              onEdit={onEdit}
              onDelete={onDelete}
              onQuickComplete={onQuickComplete}
              onView={onView}
              darkMode={darkMode}
            />
          ))
        )}
      </Box>
    </Box>
  );
}

// ─── Task Detail Dialog ───────────────────────────────────────────────────────

function TaskDetailDialog({ open, task, onClose, onEdit, darkMode }) {
  const [detailTab, setDetailTab] = useState(0);
  const [taskHistory, setTaskHistory] = useState([]);
  const [historyLoading, setHistoryLoading] = useState(false);
  const [playUrl, setPlayUrl] = useState(null);
  const [playLoading, setPlayLoading] = useState(false);
  const [downloadLoading, setDownloadLoading] = useState(false);
  const [notify, setNotify] = useState({ isOpen: false, message: "", type: "", pagename: "", status: "" });

  const filePath = task?.filePath || "";
  const fileName = filePath ? filePath.split("/").pop() : "";
  const isAudioFile = /\.(mp3|wav)$/i.test(filePath);
  const isVideoFile = /\.(mp4)$/i.test(filePath);
  const isImageFile = /\.(jpeg|jpg|png|img)$/i.test(filePath);
  const isPlayable = isAudioFile || isVideoFile || isImageFile;

  useEffect(() => {
    if (!open || !task?.id) return;
    setDetailTab(0);
    setPlayUrl(null);
    setTaskHistory([]);
    loadHistory(task.id);
  }, [open, task?.id]);

  async function loadHistory(taskId) {
    setHistoryLoading(true);
    try {
      const res = await application.post(`/taskDetails/listAll/${taskId}`);
      setTaskHistory(Array.isArray(res?.data?.data) ? res.data.data : []);
    } catch (_) {
      setTaskHistory([]);
    } finally {
      setHistoryLoading(false);
    }
  }

  async function handlePlay() {
    if (!task?.id) return;
    setPlayLoading(true);
    try {
      const response = await application.get(`/task/play/${task.id}`, { responseType: "arraybuffer" });
      const mimeType = isVideoFile
        ? "video/mp4"
        : filePath.toLowerCase().endsWith(".wav")
          ? "audio/wav"
          : isAudioFile
            ? "audio/mpeg"
            : isImageFile
              ? "image/jpeg"
              : "application/octet-stream";
      const blob = new Blob([response.data], { type: mimeType });
      setPlayUrl(URL.createObjectURL(blob));
    } catch (_) {
      setNotify({ isOpen: true, message: "", type: "error", pagename: "Task", status: "Unable to play file." });
    } finally {
      setPlayLoading(false);
    }
  }

  async function handleDownload() {
    if (!task?.id || !filePath) return;
    setDownloadLoading(true);
    try {
      const response = await application.get(`/task/download/${task.id}`, { responseType: "blob" });
      const blob = new Blob([response.data], { type: response.headers["content-type"] });
      const link = document.createElement("a");
      link.href = URL.createObjectURL(blob);
      link.download = fileName || "task-file";
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
    } catch (_) {
      setNotify({ isOpen: true, message: "", type: "error", pagename: "Task", status: "Unable to download file." });
    } finally {
      setDownloadLoading(false);
    }
  }

  if (!task) return null;

  const isCompleted = task.currentStats === "Completed";
  const pColor = priorityColor(task.taskPriority);
  const sColor = statusColor(task.currentStats);
  const overdue = isOverdue(task.expectedCompletionDate, task.currentStats);

  return (
    <Dialog open={open} onClose={onClose} maxWidth="md" fullWidth PaperProps={{ sx: { borderRadius: 2 } }}>
      {/* ── Header ── */}
      <DialogTitle sx={{ display: "flex", alignItems: "center", justifyContent: "space-between", py: 1.2, px: 2 }}>
        <Box sx={{ display: "flex", alignItems: "center", gap: 1, minWidth: 0 }}>
          <AssignmentOutlinedIcon fontSize="small" color="info" sx={{ flexShrink: 0 }} />
          <MDTypography sx={{ fontWeight: 700, fontSize: "0.92rem", mr: 0.5, flexShrink: 0 }}>
            {task.id} &#183;&nbsp;
          </MDTypography>
          <MDTypography sx={{ fontWeight: 600, fontSize: "0.85rem", overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }}>
            {task.tasksName}
          </MDTypography>
          <Chip
            label={task.currentStats}
            size="small"
            color={sColor}
            sx={(t) => ({ ml: 0.5, height: 18, fontSize: "0.6rem", flexShrink: 0, "& .MuiChip-label": { px: 0.6 }, ...(darkMode && sColor !== "default" && t.palette[sColor] && { backgroundColor: t.palette[sColor].main, color: "#fff" }) })}
          />
        </Box>
        <IconButton size="small" onClick={onClose} sx={{ flexShrink: 0 }}>
          <CloseIcon fontSize="small" color="error" />
        </IconButton>
      </DialogTitle>

      <Divider sx={{ my: 0 }} />

      {/* ── Tabs ── */}
      <Box sx={{ px: 2 }}>
        <Tabs
          value={detailTab}
          onChange={(_, v) => setDetailTab(v)}
          sx={{
            minHeight: 36,
            "& .MuiTab-root": {
              minHeight: 36,
              textTransform: "none",
              fontSize: "0.78rem",
              py: 0,
              px: 1.5,
              // borderRadius: "6px 6px 0 0",
            },
            "& .MuiTab-root.Mui-selected": {
              backgroundColor: "#1976d2",
              color: "#fff",
            },
            // "& .MuiTabs-indicator": {
            //   height: 2,
            // },
          }}
        >
          <Tab label="Details" />
          <Tab
            label={
              <Box sx={{ display: "flex", alignItems: "center", gap: 0.5 }}>
                <HistoryOutlinedIcon sx={{ fontSize: 14 }} />
                {historyLoading ? "History…" : `History${taskHistory.length > 0 ? ` (${taskHistory.length})` : ""}`}
              </Box>
            }
          />
        </Tabs>
      </Box>

      <DialogContent sx={{ pt: 1, pb: 1, px: 2 }}>
        {/* ── Details Tab ── */}
        {detailTab === 0 && (
          <Grid container spacing={1.5}>

            {/* Customer row */}
            {(task.name || task.contactNo) && (
              <Grid item xs={12}>
                <Box
                  sx={{
                    display: "flex",
                    alignItems: "center",
                    gap: 2,
                    p: 1,
                    borderRadius: 1,
                    backgroundColor: (t) => alpha(t.palette.info.main, 0.05),
                    border: (t) => `1px solid ${alpha(t.palette.info.main, 0.18)}`,
                  }}
                >
                  {task.name && (
                    <Box sx={{ display: "flex", alignItems: "center", gap: 0.5 }}>
                      <PersonOutlineOutlinedIcon sx={{ fontSize: 15, color: "info.main" }} />
                      <MDTypography sx={{ fontSize: "0.82rem", fontWeight: 600 }}>{task.name}</MDTypography>
                    </Box>
                  )}
                  {task.contactNo && (
                    <Box sx={{ display: "flex", alignItems: "center", gap: 0.5 }}>
                      <CallOutlinedIcon sx={{ fontSize: 15, color: "success.main" }} />
                      <MDTypography sx={{ fontSize: "0.82rem" }}>{task.contactNo}</MDTypography>
                    </Box>
                  )}
                </Box>
              </Grid>
            )}

            {/* Priority + Type */}
            <Grid item xs={6}>
              <MDTypography sx={{ fontSize: "0.65rem", color: "text", mb: 0.4, textTransform: "uppercase", letterSpacing: "0.04em" }}>Priority</MDTypography>
              {task.taskPriority ? (
                <Chip
                  label={task.taskPriority}
                  size="small"
                  color={pColor}
                  variant="outlined"
                  sx={(t) => ({ height: 20, fontSize: "0.67rem", "& .MuiChip-label": { px: 0.65 }, ...(darkMode && pColor !== "default" && t.palette[pColor] && { color: t.palette[pColor].main, borderColor: t.palette[pColor].main }) })}
                />
              ) : <MDTypography sx={{ fontSize: "0.78rem" }}>—</MDTypography>}
            </Grid>
            <Grid item xs={6}>
              <MDTypography sx={{ fontSize: "0.65rem", color: "text", mb: 0.4, textTransform: "uppercase", letterSpacing: "0.04em" }}>Task Type</MDTypography>
              {task.taskType ? (
                <Chip label={task.taskType} size="small" variant="outlined" sx={{ height: 20, fontSize: "0.67rem", "& .MuiChip-label": { px: 0.65 } }} />
              ) : <MDTypography sx={{ fontSize: "0.78rem" }}>—</MDTypography>}
            </Grid>

            {/* Assignee + Reviewer */}
            <Grid item xs={6}>
              <MDTypography sx={{ fontSize: "0.65rem", color: "text", mb: 0.3, textTransform: "uppercase", letterSpacing: "0.04em" }}>Assigned To</MDTypography>
              <Box sx={{ display: "flex", alignItems: "center", gap: 0.4 }}>
                <PersonOutlineOutlinedIcon sx={{ fontSize: 13, color: "text" }} />
                <MDTypography sx={{ fontSize: "0.8rem" }}>{task.assignedUser || task.assignedGroup || "—"}</MDTypography>
              </Box>
            </Grid>
            <Grid item xs={6}>
              <MDTypography sx={{ fontSize: "0.65rem", color: "text", mb: 0.3, textTransform: "uppercase", letterSpacing: "0.04em" }}>Reviewer</MDTypography>
              <MDTypography sx={{ fontSize: "0.8rem" }}>{task.reviewer || "—"}</MDTypography>
            </Grid>

            {/* Dates */}
            <Grid item xs={6}>
              <MDTypography sx={{ fontSize: "0.65rem", color: "text", mb: 0.3, textTransform: "uppercase", letterSpacing: "0.04em" }}>Start Date</MDTypography>
              <Box sx={{ display: "flex", alignItems: "center", gap: 0.4 }}>
                <AccessTimeOutlinedIcon sx={{ fontSize: 13, color: "text" }} />
                <MDTypography sx={{ fontSize: "0.8rem" }}>{fmtDate(task.assignedOn)}</MDTypography>
              </Box>
            </Grid>
            <Grid item xs={6}>
              <MDTypography sx={{ fontSize: "0.65rem", color: "text", mb: 0.3, textTransform: "uppercase", letterSpacing: "0.04em" }}>Due Date</MDTypography>
              <Box sx={{ display: "flex", alignItems: "center", gap: 0.4 }}>
                <AccessTimeOutlinedIcon sx={{ fontSize: 13, color: overdue ? "error.main" : "text" }} />
                <MDTypography sx={{ fontSize: "0.8rem", color: overdue ? "error.main" : "inherit", fontWeight: overdue ? 700 : 400 }}>
                  {fmtDate(task.expectedCompletionDate)}{overdue ? " (Overdue)" : ""}
                </MDTypography>
              </Box>
            </Grid>

            {/* Completed On */}
            {task.taskCompletedOn && (
              <Grid item xs={6}>
                <MDTypography sx={{ fontSize: "0.65rem", color: "text", mb: 0.3, textTransform: "uppercase", letterSpacing: "0.04em" }}>Completed On</MDTypography>
                <MDTypography sx={{ fontSize: "0.8rem", color: "success.main" }}>{fmtDate(task.taskCompletedOn)}</MDTypography>
              </Grid>
            )}

            {/* Description */}
            {task.taskDescription && (
              <Grid item xs={12}>
                <MDTypography sx={{ fontSize: "0.65rem", color: "text", mb: 0.4, textTransform: "uppercase", letterSpacing: "0.04em" }}>Description</MDTypography>
                <Box sx={{ p: 1, borderRadius: 1, backgroundColor: (t) => alpha(t.palette.background.default, 0.7), border: (t) => `1px solid ${alpha(t.palette.divider, 0.5)}` }}>
                  <MDTypography sx={{ fontSize: "0.8rem", lineHeight: 1.6 }}>{task.taskDescription}</MDTypography>
                </Box>
              </Grid>
            )}

            {/* File Section */}
            <Grid item xs={12}>
              <MDTypography sx={{ fontSize: "0.65rem", color: "text", mb: 0.4, textTransform: "uppercase", letterSpacing: "0.04em" }}>Attached File</MDTypography>
              <Box
                sx={{
                  p: 1,
                  borderRadius: 1,
                  border: (t) => `1px solid ${alpha(filePath ? t.palette.info.main : t.palette.divider, filePath ? 0.3 : 0.5)}`,
                  display: "flex",
                  alignItems: "center",
                  gap: 1,
                  backgroundColor: (t) => filePath ? alpha(t.palette.info.main, 0.04) : "transparent",
                }}
              >
                <AttachFileOutlinedIcon sx={{ fontSize: 16, color: filePath ? "info.main" : "text", flexShrink: 0 }} />
                <MDTypography sx={{ flex: 1, fontSize: "0.78rem", color: filePath ? "text" : "text", overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }}>
                  {fileName || "No file attached"}
                </MDTypography>
                {filePath && isPlayable && (
                  <Tooltip title={isAudioFile ? "Play Audio" : isVideoFile ? "Play Video" : "View Image"}>
                    <IconButton size="small" sx={{ p: 0.5, color: "success.main" }} onClick={handlePlay} disabled={playLoading}>
                      {playLoading ? <CircularProgress size={13} color="inherit" /> : <PlayCircleOutlineIcon sx={{ fontSize: 17 }} />}
                    </IconButton>
                  </Tooltip>
                )}
                {filePath && (
                  <Tooltip title="Download File">
                    <IconButton size="small" sx={{ p: 0.5, color: "info.main" }} onClick={handleDownload} disabled={downloadLoading}>
                      {downloadLoading ? <CircularProgress size={13} color="inherit" /> : <DownloadOutlinedIcon sx={{ fontSize: 17 }} />}
                    </IconButton>
                  </Tooltip>
                )}
              </Box>

              {/* Inline player */}
              {playUrl && (
                <Box sx={{ mt: 1, p: 1, borderRadius: 1, overflow: "hidden", border: (t) => `1px solid ${alpha(t.palette.divider, 0.5)}`, p: 0.75, position: "relative" }}>
                  <IconButton
                    size="small"
                    sx={{ position: "absolute", top: 12, right: 1.5, p: 0.3, color: "error.main", zIndex: 1 }}
                    onClick={() => setPlayUrl(null)}
                  >
                    <CloseIcon sx={{ fontSize: 13 }} />
                  </IconButton>
                  {isAudioFile && <audio src={playUrl} controls autoPlay style={{ width: "100%", height: 36 }} />}
                  {isVideoFile && <video src={playUrl} controls autoPlay style={{ width: "100%", maxHeight: 260 }} />}
                  {isImageFile && <img src={playUrl} alt="attachment" style={{ maxWidth: "100%", maxHeight: 280, objectFit: "contain", display: "block", margin: "0 auto" }} />}
                </Box>
              )}
            </Grid>
          </Grid>
        )}

        {/* ── History Tab ── */}
        {detailTab === 1 && (
          <Box>
            {historyLoading ? (
              <Box sx={{ display: "flex", justifyContent: "center", py: 4 }}>
                <CircularProgress size={26} />
              </Box>
            ) : taskHistory.length === 0 ? (
              <Box sx={{ textAlign: "center", py: 4 }}>
                <HistoryOutlinedIcon sx={{ fontSize: 32, color: "text", mb: 1 }} />
                <MDTypography sx={{ fontSize: "0.8rem", color: "text" }}>No history available</MDTypography>
              </Box>
            ) : (
              <Box sx={{ display: "flex", flexDirection: "column", gap: 0.75 }}>
                {taskHistory.map((row, idx) => {
                  const hSColor = statusColor(row.taskStats);
                  return (
                    <Box
                      key={idx}
                      sx={{
                        borderRadius: 1.5,
                        border: (t) => `1px solid ${alpha(t.palette.divider, 0.45)}`,
                        borderLeft: (t) => `3px solid ${t.palette[hSColor]?.main || t.palette.grey[400]}`,
                        p: "10px 12px",
                        backgroundColor: (t) => alpha(t.palette.background.default, 0.5),
                        "&:hover": {
                          backgroundColor: (t) => alpha(t.palette.info.main, 0.04),
                        },
                      }}
                    >
                      {/* Top row: status chip + updated by + date (right-aligned) */}
                      <Box sx={{ display: "flex", alignItems: "center", gap: 1, mb: row.changedCompletionOn || row.remarks ? 0.75 : 0 }}>
                        {row.taskStats && (
                          <Chip
                            label={row.taskStats}
                            size="small"
                            color={hSColor}
                            sx={(t) => ({
                              height: 18,
                              fontSize: "0.62rem",
                              fontWeight: 700,
                              flexShrink: 0,
                              "& .MuiChip-label": { px: 0.6 },
                              ...(darkMode && hSColor !== "default" && t.palette[hSColor] && {
                                backgroundColor: t.palette[hSColor].main,
                                color: "#fff",
                              }),
                            })}
                          />
                        )}
                        {row.statusUpdatedBy && (
                          <Box sx={{ display: "flex", alignItems: "center", gap: 0.35, minWidth: 0 }}>
                            <PersonOutlineOutlinedIcon sx={{ fontSize: 12, color: "text", flexShrink: 0 }} />
                            <MDTypography sx={{ fontSize: "0.73rem", fontWeight: 600, overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }}>
                              {row.statusUpdatedBy}
                            </MDTypography>
                          </Box>
                        )}
                        <Box sx={{ ml: "auto", display: "flex", alignItems: "center", gap: 0.35, flexShrink: 0 }}>
                          <AccessTimeOutlinedIcon sx={{ fontSize: 12, color: "text" }} />
                          <MDTypography sx={{ fontSize: "0.7rem", color: "text", whiteSpace: "nowrap" }}>
                            {row.statusUpdatedOn || "—"}
                          </MDTypography>
                        </Box>
                      </Box>

                      {/* Bottom row: changed completion + remarks */}
                      {(row.changedCompletionOn || row.remarks) && (
                        <Box sx={{ display: "flex", gap: 2, flexWrap: "wrap", pt: 0.5, borderTop: (t) => `1px dashed ${alpha(t.palette.divider, 0.4)}` }}>
                          {row.changedCompletionOn && (
                            <Box>
                              <MDTypography sx={{ fontSize: "0.6rem", color: "text", textTransform: "uppercase", letterSpacing: "0.04em", mb: 0.1 }}>
                                Changed Completion
                              </MDTypography>
                              <MDTypography sx={{ fontSize: "0.72rem" }}>
                                {row.changedCompletionOn}
                              </MDTypography>
                            </Box>
                          )}
                          {row.remarks && (
                            <Box sx={{ flex: 1, minWidth: 0 }}>
                              <MDTypography sx={{ fontSize: "0.6rem", color: "text", textTransform: "uppercase", letterSpacing: "0.04em", mb: 0.1 }}>
                                Remarks
                              </MDTypography>
                              <MDTypography sx={{ fontSize: "0.72rem", wordBreak: "break-word", lineHeight: 1.5 }}>
                                {row.remarks}
                              </MDTypography>
                            </Box>
                          )}
                        </Box>
                      )}
                    </Box>
                  );
                })}
              </Box>
            )}
          </Box>
        )}
      </DialogContent>

      <DialogActions sx={{ px: 2, pb: 1.5, gap: 1 }}>
        <MDButton size="small" variant="outlined" color="secondary" onClick={onClose}>
          Close
        </MDButton>
        {!isCompleted && (
          <MDButton
            size="small"
            variant="gradient"
            color="info"
            onClick={() => { onClose(); onEdit(task); }}
          >
            <Box sx={{ display: "flex", alignItems: "center", gap: 0.5 }}>
              <EditOutlinedIcon sx={{ fontSize: 14 }} />
              Edit Task
            </Box>
          </MDButton>
        )}
      </DialogActions>

      <CTLNotification notify={notify} setNotify={setNotify} />
    </Dialog>
  );
}

// ─── Main Component ───────────────────────────────────────────────────────────

export default function DispatcherTaskPanel({
  onLoad = () => { },
  searchText = "",
  registerCallBacks,
  pageName = "Task Management",
}) {
  const [controller] = useMaterialUIController();
  const { activePage, darkMode } = controller;

  // ── State ──
  const [tasks, setTasks] = useState([]);
  const [loading, setLoading] = useState(false);
  const [statusFilter, setStatusFilter] = useState("");
  const [view, setView] = useState("list");
  const [openForm, setOpenForm] = useState(false);
  const [formData, setFormData] = useState(EMPTY_FORM);
  const [isEditMode, setIsEditMode] = useState(false);
  const [saving, setSaving] = useState(false);
  const [fileUploading, setFileUploading] = useState(false);
  const [localFileName, setLocalFileName] = useState("");
  const [confirmDeleteId, setConfirmDeleteId] = useState(null);
  const [priorityOpts, setPriorityOpts] = useState([]);
  const [typeOpts, setTypeOpts] = useState([]);
  const [userOpts, setUserOpts] = useState([]);
  const [reviewerOpts, setReviewerOpts] = useState([]);
  const [groupOpts, setGroupOpts] = useState([]);
  const [viewTask, setViewTask] = useState(null);
  const [userSearch, setUserSearch] = useState("");
  const [groupSearch, setGroupSearch] = useState("");
  const [notify, setNotify] = useState({
    isOpen: false,
    message: "",
    type: "",
    pagename: "",
    status: "",
  });

  const refreshTimerRef = useRef(null);

  // ── Register page attributes & callbacks ──
  useEffect(() => {
    onLoad(PAGE_ATTRIBUTES);
    registerCallBacks && registerCallBacks({ onRefresh: loadTasks });
  }, [activePage]);

  // ── Fetch option lists once ──
  useEffect(() => {
    loadOptions();
  }, []);

  // ── Load tasks when statusFilter changes ──
  useEffect(() => {
    loadTasks();
  }, [statusFilter]);

  // ── Auto-refresh every 30 s ──
  useEffect(() => {
    refreshTimerRef.current = window.setInterval(loadTasks, 30000);
    return () => window.clearInterval(refreshTimerRef.current);
  }, [statusFilter]);

  // ─────────────────────────────────────────────────────────────────────────

  async function loadTasks() {
    setLoading(true);
    try {
      let url;
      if (!statusFilter) {
        url = API_LIST;
      } else if (statusFilter === "delayed") {
        url = API_DELAYED_LIST;
      } else {
        url = API_LIST_BY_STATUS(statusFilter);
      }

      const res = await application.post(url, {
        currentPage: 0,
        pageSize: 500,
        sortDirection: "desc",
        sortBy: "assignedOn",
        sortDataType: "date",
        search: "",
        advancedFilters: [],
      });

      // paginated response: data.data.currentPageData
      // non-paginated response: data.data (array directly)
      const payload = res?.data?.data;
      const raw = Array.isArray(payload)
        ? payload
        : Array.isArray(payload?.currentPageData)
          ? payload.currentPageData
          : [];
      setTasks(raw);
    } catch (err) {
      setNotify({
        isOpen: true,
        message: "",
        type: "error",
        pagename: pageName,
        status: err?.response?.data?.message || "Unable to load tasks.",
      });
    } finally {
      setLoading(false);
    }
  }

  async function loadOptions() {
    try {
      const [priRes, typeRes, userRes, revRes, grpRes] = await Promise.allSettled([
        application.post(API_PRIORITY_OPTIONS),
        application.post(API_TYPE_OPTIONS),
        application.post(API_USER_OPTIONS),
        application.post(API_REVIEWER_OPTIONS),
        application.post(API_GROUP_OPTIONS),
      ]);

      if (priRes.status === "fulfilled") {
        const d = priRes.value?.data?.data;
        setPriorityOpts(Array.isArray(d) ? d.map((x) => x.taskPriority).filter(Boolean) : []);
      }
      if (typeRes.status === "fulfilled") {
        const d = typeRes.value?.data?.data;
        setTypeOpts(Array.isArray(d) ? d.map((x) => x.taskType).filter(Boolean) : []);
      }
      if (userRes.status === "fulfilled") {
        const d = userRes.value?.data?.data;
        setUserOpts(Array.isArray(d) ? d.map((x) => x.user).filter(Boolean) : []);
      }
      if (revRes.status === "fulfilled") {
        const d = revRes.value?.data?.data;
        setReviewerOpts(Array.isArray(d) ? d.map((x) => x.user).filter(Boolean) : []);
      }
      if (grpRes.status === "fulfilled") {
        const d = grpRes.value?.data?.data;
        setGroupOpts(Array.isArray(d) ? d.map((x) => x.code).filter(Boolean) : []);
      }
    } catch (_) {
      // non-critical
    }
  }

  // ─── Filtered task list ───────────────────────────────────────────────────

  const filteredTasks = useMemo(() => {
    if (!searchText) return tasks;
    const q = searchText.toLowerCase();
    return tasks.filter(
      (t) =>
        (t.tasksName || "").toLowerCase().includes(q) ||
        (t.assignedUser || "").toLowerCase().includes(q) ||
        (t.currentStats || "").toLowerCase().includes(q) ||
        (t.taskPriority || "").toLowerCase().includes(q) ||
        (t.taskType || "").toLowerCase().includes(q) ||
        (t.name || "").toLowerCase().includes(q) ||
        String(t.id || "").includes(q),
    );
  }, [tasks, searchText]);

  const tasksByBoardStatus = useMemo(() => {
    const groups = {};
    BOARD_COLUMNS.forEach((col) => {
      groups[col.statusKey] = [];
    });
    filteredTasks.forEach((task) => {
      const s = task.currentStats || "";
      if (groups[s] !== undefined) {
        groups[s].push(task);
      }
    });
    return groups;
  }, [filteredTasks]);

  // ─── Form handlers ────────────────────────────────────────────────────────

  function openCreateForm() {
    setFormData({ ...EMPTY_FORM, assignedOn: nowLocal() });
    setIsEditMode(false);
    setLocalFileName("");
    setOpenForm(true);
  }

  function openEditForm(task) {
    if (task.currentStats === "Completed") {
      setNotify({
        isOpen: true,
        message: `Completed on: ${task.taskCompletedOn || "—"}`,
        type: "warning",
        pagename: pageName,
        status: "Cannot edit a completed task.",
      });
      return;
    }
    const existingFilePath = task.filePath || "";
    setFormData({
      ...task,
      assignedOn: isoToLocal(task.assignedOn),
      expectedCompletionDate: isoToLocal(task.expectedCompletionDate),
      assignedTo: task.assignedTo ? String(task.assignedTo) : (task.assignedUser ? "2" : "1"),
      filePath: existingFilePath,
    });
    setLocalFileName(existingFilePath ? existingFilePath.split("/").pop() || existingFilePath : "");
    setIsEditMode(true);
    setOpenForm(true);
  }

  function setField(key, value) {
    setFormData((prev) => ({ ...prev, [key]: value }));
  }

  async function handleFileUpload(file) {
    if (!file) return;
    setLocalFileName(file.name);
    setFileUploading(true);
    try {
      const fd = new FormData();
      fd.append("file", file);
      fd.append("type", "default");
      fd.append("selectedFile", file.name);
      const res = await application.post(API_FILE_UPLOAD, fd, {
        headers: { "Content-Type": "multipart/form-data" },
      });
      // TaskManager reads filePath from response.data.messageDetail
      const path = res?.data?.messageDetail || res?.data?.data || res?.data?.filePath || "";
      setField("filePath", path);
      const displayName = path ? path.split("/").pop() || path : file.name;
      setLocalFileName(displayName);
      setNotify({ isOpen: true, message: "", type: "success", pagename: pageName, status: `File "${displayName}" uploaded successfully.` });
    } catch (err) {
      setLocalFileName("");
      setNotify({ isOpen: true, message: "", type: "error", pagename: pageName, status: err?.response?.data?.message || "File upload failed." });
    } finally {
      setFileUploading(false);
    }
  }

  async function handleSave() {
    const { tasksName, taskPriority, taskType, assignedOn, expectedCompletionDate } = formData;
    if (!tasksName?.trim() || !taskPriority || !taskType || !assignedOn || !expectedCompletionDate) {
      setNotify({
        isOpen: true,
        message: "",
        type: "warning",
        pagename: pageName,
        status: "Subject, Priority, Type, Start Date and Due Date are required.",
      });
      return;
    }

    setSaving(true);
    try {
      const assignedToInt = formData.assignedTo ? parseInt(formData.assignedTo, 10) : 2;
      const payload = {
        id: isEditMode ? formData.id : 0,
        name: formData.name || "",
        contactNo: formData.contactNo || "",
        tasksName: formData.tasksName,
        expectedCompletionDate: localToApi(formData.expectedCompletionDate),
        taskPriority: formData.taskPriority,
        taskType: formData.taskType,
        assignedTo: assignedToInt,
        ...(assignedToInt === 2
          ? { assignedUser: formData.assignedUser || "" }
          : { assignedGroup: formData.assignedGroup || "" }),
        reviewer: formData.reviewer || "",
        taskDescription: formData.taskDescription || "",
        filePath: formData.filePath || "",
        assignedOn: localToApi(formData.assignedOn),
        currentStats: isEditMode ? formData.currentStats : "New",
      };

      if (isEditMode) {
        const orig = tasks.find((t) => t.id === formData.id) || {};
        const changed = Object.keys(payload).filter(
          (k) => payload[k] !== orig[k] && k !== "assignedOn" && k !== "expectedCompletionDate",
        );
        const remarks = changed
          .filter((k) => ["taskPriority", "currentStats", "taskType", "assignedUser", "taskDescription"].includes(k))
          .map((k) => `${k} changed from "${orig[k]}" to "${payload[k]}"`)
          .join("; ");
        payload.remarks = remarks;
        if (payload.currentStats === "Closed" || payload.currentStats === "Completed") {
          payload.taskCompletedOn = nowApiFormat();
          payload.currentStats = "Completed";
        }
      }

      const url = isEditMode ? API_UPDATE : API_CREATE;
      const res = await application.post(url, payload);
      setNotify({
        isOpen: true,
        message: "",
        type: "success",
        pagename: pageName,
        status: res?.data?.messageDetail || `Task ${isEditMode ? "updated" : "created"} successfully.`,
      });
      setOpenForm(false);
      loadTasks();
    } catch (err) {
      setNotify({
        isOpen: true,
        message: "",
        type: "error",
        pagename: pageName,
        status:
          err?.response?.data?.messageDetail ||
          err?.response?.data?.message ||
          "Failed to save task.",
      });
    } finally {
      setSaving(false);
    }
  }

  async function handleDelete(id) {
    try {
      const res = await application.post(API_DELETE, { intId: [id] });
      setNotify({
        isOpen: true,
        message: "",
        type: "success",
        pagename: pageName,
        status: res?.data?.messageDetail || "Task deleted.",
      });
      loadTasks();
    } catch (err) {
      setNotify({
        isOpen: true,
        message: "",
        type: "error",
        pagename: pageName,
        status: err?.response?.data?.message || "Failed to delete task.",
      });
    }
    setConfirmDeleteId(null);
  }

  async function handleQuickComplete(task) {
    const payload = {
      ...task,
      assignedOn: localToApi(isoToLocal(task.assignedOn)),
      expectedCompletionDate: localToApi(isoToLocal(task.expectedCompletionDate)),
      currentStats: "Completed",
      taskCompletedOn: nowApiFormat(),
      remarks: `Status changed to Completed`,
    };
    try {
      await application.post(API_UPDATE, payload);
      loadTasks();
    } catch (_) { }
  }

  // ─── Shared card handlers passed down ────────────────────────────────────

  const handleEdit = openEditForm;
  const handleDeleteReq = (id) => setConfirmDeleteId(id);
  const handleQuickCompleteCallback = handleQuickComplete;
  const handleOpenDetail = (task) => setViewTask(task);

  // ─── Render ───────────────────────────────────────────────────────────────

  return (
    <MDBox
      sx={{
        display: "flex",
        flexDirection: "column",
        height: "100%",
        minHeight: 0,
      }}
    >
      {/* ── Header Bar ── */}
      <Box
        sx={{
          display: "flex",
          alignItems: "center",
          justifyContent: "space-between",
          px: 1,
          py: 0.55,
          flexShrink: 0,
          border: (t) => `1px solid ${darkMode ? alpha("#ffffff", 0.35) : alpha("#000000", 0.35)}`,
          borderRadius: 1,
        }}
      >
        <Box sx={{ display: "flex", alignItems: "center", gap: 0.5 }}>
          <AssignmentOutlinedIcon sx={{ fontSize: 15, color: "info.main" }} />
          {loading ? (
           <>
             <MDTypography sx={{ fontSize: "0.7rem", color: "text" }}>
                <CircularProgress size={11} sx={{ ml: 0.5 }} /> 
               Loading...</MDTypography>
            </>
            
          ) : (
            <Chip
              label={`total tasks: ${filteredTasks.length}`}
              size="small"
              color="info"
              sx={(t) => ({
                height: 16,
                marginRight: 1.5,
                fontSize: "0.6rem",
                "& .MuiChip-label": { px: 0.5, py: 0.5 },
                ...(darkMode && { backgroundColor: t.palette.info.main, color: "#fff" }),
              })}
            />
          )}

           {STATUS_FILTERS.map((sf) => (
          <Chip
            key={sf.key}
            label={sf.label}
            size="small"
            clickable
            color={statusFilter === sf.key ? sf.color || "info" : "default"}
            variant={statusFilter === sf.key ? "filled" : "outlined"}
            onClick={() => setStatusFilter(sf.key)}
            sx={(t) => {
              const activeColor = sf.color || "info";
              const isActive = statusFilter === sf.key;

              return {
                height: 23,
                width: "fit-content",
                fontSize: "0.6rem",
                fontWeight: isActive ? 700 : 400,
                "& .MuiChip-label": { px: 0.55 },
                transition: "all 0.15s ease",

                ...(isActive &&
                  activeColor !== "default" &&
                  t.palette[activeColor] && {
                  backgroundColor: t.palette[activeColor].main,
                  color: "#fff",
                  borderColor: t.palette[activeColor].main,
                }),

                ...(!isActive &&
                  activeColor !== "default" &&
                  t.palette[activeColor] && {
                  color: t.palette[activeColor].main,
                  borderColor: t.palette[activeColor].main,
                }),
              };
            }}
          />
        ))}
        </Box>

        <Box sx={{ display: "flex", gap: 0.4, alignItems: "center" }}>
          <Tooltip title={view === "list" ? "Board View" : "List View"}>
            <IconButton
              size="small"
              sx={{ p: 0.3 }}
              onClick={() => setView((v) => (v === "list" ? "board" : "list"))}
            >
              {view === "list" ? (
                <ViewColumnOutlinedIcon sx={{ fontSize: 14, color: "info.main" }} />
              ) : (
                <ViewListOutlinedIcon sx={{ fontSize: 14, color: "info.main" }} />
              )}
            </IconButton>
          </Tooltip>

          <Tooltip title="Refresh">
            <IconButton
              size="small"
              sx={{ p: 0.3 }}
              onClick={loadTasks}
              disabled={loading}
            >
              <RefreshOutlinedIcon sx={{ fontSize: 14, color: "info.main" }} />
            </IconButton>
          </Tooltip>

          <Tooltip title="New Task">
            <IconButton
              size="small"
              onClick={openCreateForm}
              sx={{
                p: 0.3,
                color: "success.main",
                border: (t) => `1px solid ${alpha(t.palette.success.main, 0.4)}`,
                backgroundColor: (t) => alpha(t.palette.success.main, 0.08),
                "&:hover": {
                  backgroundColor: (t) => alpha(t.palette.success.main, 0.16),
                },
              }}
            >
              <AddCircleOutlineIcon sx={{ fontSize: 14 }} />
            </IconButton>
          </Tooltip>
        </Box>
      </Box>

      {/* ── Task Content ── */}
      <Box
        sx={{
          flex: 1,
          minHeight: 0,
          overflow: "hidden",
          display: "flex",
          flexDirection: "column",
        }}
      >
        {loading && filteredTasks.length === 0 ? (
          <Box sx={{ display: "flex", justifyContent: "center", alignItems: "center", flex: 1 }}>
            <CircularProgress size={26} />
          </Box>
        ) : filteredTasks.length === 0 ? (
          <Box
            sx={{
              display: "flex",
              flexDirection: "column",
              alignItems: "center",
              justifyContent: "center",
              flex: 1,
              gap: 0.75,
              color: "text",
            }}
          >
            <AssignmentOutlinedIcon sx={{ fontSize: 30 }} />
            <MDTypography sx={{ fontSize: "0.72rem", color: "text" }}>
              No tasks found
            </MDTypography>
          </Box>
        ) : view === "list" ? (
          /* ── List View ── */
          <Box
            sx={{
              flex: 1,
              overflowY: "auto",
              px: 0.75,
              pt: 0.6,
              pb: 0.5,
              scrollbarWidth: "thin",
              "&::-webkit-scrollbar": { width: 4 },
              "&::-webkit-scrollbar-thumb": {
                borderRadius: 2,
                backgroundColor: (t) => alpha(t.palette.info.main, 0.3),
              },
            }}
          >
            {filteredTasks.map((task) => (
              <TaskCard
                key={task.id}
                task={task}
                onEdit={handleEdit}
                onDelete={handleDeleteReq}
                onQuickComplete={handleQuickCompleteCallback}
                onView={handleOpenDetail}
                darkMode={darkMode}
              />
            ))}
          </Box>
        ) : (
          /* ── Board View ── */
          <Box
            sx={{
              flex: 1,
              display: "flex",
              gap: 0.6,
              overflowX: "auto",
              overflowY: "hidden",
              px: 0.6,
              pt: 0.6,
              pb: 0.5,
              scrollbarWidth: "thin",
              "&::-webkit-scrollbar": { height: 4 },
              "&::-webkit-scrollbar-thumb": {
                borderRadius: 2,
                backgroundColor: (t) => alpha(t.palette.info.main, 0.3),
              },
            }}
          >
            {BOARD_COLUMNS.map((col) => (
              <BoardColumn
                key={col.statusKey}
                column={col}
                tasks={tasksByBoardStatus[col.statusKey] || []}
                onEdit={handleEdit}
                onDelete={handleDeleteReq}
                onQuickComplete={handleQuickCompleteCallback}
                onView={handleOpenDetail}
                darkMode={darkMode}
              />
            ))}
          </Box>
        )}
      </Box>

      {/* ── Create / Edit Dialog ── */}
      <Dialog
        open={openForm}
        onClose={() => !saving && setOpenForm(false)}
        maxWidth="sm"
        fullWidth
        PaperProps={{ sx: { borderRadius: 2 } }}
      >
        <DialogTitle
          sx={{
            display: "flex",
            alignItems: "center",
            justifyContent: "space-between",
            py: 1.2,
            px: 2,
          }}
        >
          <Box sx={{ display: "flex", alignItems: "center", gap: 0.75 }}>
            <AssignmentOutlinedIcon fontSize="small" color="info" />
            <MDTypography sx={{ fontWeight: 700, fontSize: "0.92rem" }}>
              {isEditMode ? "Edit Task" : "New Task"}
            </MDTypography>
          </Box>
          <IconButton size="small" onClick={() => setOpenForm(false)} disabled={saving}>
            <CloseIcon fontSize="small" />
          </IconButton>
        </DialogTitle>

        <Divider sx={{ my: 0 }} />

        <DialogContent sx={{ pt: 2, pb: 1 }}>
          <Grid container spacing={1.5}>

            {/* ── Row 1: Customer Name + Contact No ── */}
            <Grid item xs={6}>
              <TextField
                label="Customer Name"
                fullWidth
                value={formData.name || ""}
                onChange={(e) => setField("name", e.target.value)}
                sx={{ "& .MuiInputBase-root": { height: 35 } }}
              />
            </Grid>
            <Grid item xs={6}>
              <TextField
                label="Contact No"
                fullWidth
                value={formData.contactNo || ""}
                onChange={(e) => setField("contactNo", e.target.value.replace(/\D/g, ""))}
                sx={{ "& .MuiInputBase-root": { height: 35 } }}
              />
            </Grid>

            {/* ── Row 2: Task Subject ── */}
            <Grid item xs={12}>
              <TextField
                label="Task Subject"
                fullWidth
                required
                value={formData.tasksName || ""}
                onChange={(e) => setField("tasksName", e.target.value)}
                sx={{ "& .MuiInputBase-root": { height: 35 } }}
              />
            </Grid>

            {/* ── Row 3: Priority + Task Type ── */}
            <Grid item xs={6}>
              <FormControl fullWidth required sx={{ "& .MuiInputBase-root": { height: 35 } }}>
                <InputLabel>Priority</InputLabel>
                <Select
                  value={formData.taskPriority || ""}
                  label="Priority"
                  onChange={(e) => setField("taskPriority", e.target.value)}
                >
                  <MenuItem value=""><em>— Select —</em></MenuItem>
                  {priorityOpts.map((o) => <MenuItem key={o} value={o}>{o}</MenuItem>)}
                </Select>
              </FormControl>
            </Grid>
            <Grid item xs={6}>
              <FormControl fullWidth required sx={{ "& .MuiInputBase-root": { height: 35 } }}>
                <InputLabel>Task Type</InputLabel>
                <Select
                  value={formData.taskType || ""}
                  label="Task Type"
                  onChange={(e) => setField("taskType", e.target.value)}
                >
                  <MenuItem value=""><em>— Select —</em></MenuItem>
                  {typeOpts.map((o) => <MenuItem key={o} value={o}>{o}</MenuItem>)}
                </Select>
              </FormControl>
            </Grid>

            {/* ── Row 4: Start Date + Due Date ── */}
            <Grid item xs={6}>
              <TextField
                label="Start Date"
                type="datetime-local"
                fullWidth
                required
                value={formData.assignedOn || ""}
                onChange={(e) => setField("assignedOn", e.target.value)}
                InputLabelProps={{ shrink: true }}
                sx={{ "& .MuiInputBase-root": { height: 35 } }}
              />
            </Grid>
            <Grid item xs={6}>
              <TextField
                label="Due Date"
                type="datetime-local"
                fullWidth
                required
                value={formData.expectedCompletionDate || ""}
                onChange={(e) => setField("expectedCompletionDate", e.target.value)}
                InputLabelProps={{ shrink: true }}
                sx={{ "& .MuiInputBase-root": { height: 35 } }}
              />
            </Grid>

            {/* ── Row 5: Assign To toggle ── */}
            <Grid item xs={6}>
              <FormControl fullWidth sx={{ "& .MuiInputBase-root": { height: 35 } }}>
                <InputLabel>Assign To</InputLabel>
                <Select
                  value={formData.assignedTo || ""}
                  label="Assign To"
                  onChange={(e) => {
                    const v = e.target.value;
                    setFormData((prev) => ({
                      ...prev,
                      assignedTo: v,
                      assignedUser: "",
                      assignedGroup: "",
                    }));
                  }}
                >
                  <MenuItem value=""><em>— Select —</em></MenuItem>
                  <MenuItem value="2">Individual</MenuItem>
                  <MenuItem value="1">Group</MenuItem>
                </Select>
              </FormControl>
            </Grid>

            {/* ── Conditional: User or Group dropdown ── */}
            {formData.assignedTo === "2" || formData.assignedTo === 2 ? (
              <Grid item xs={6}>
                <FormControl fullWidth sx={{ "& .MuiInputBase-root": { height: 35 } }}>
                  <InputLabel>Assignee User</InputLabel>
                  <Select
                    value={formData.assignedUser || ""}
                    label="Assignee User"
                    onChange={(e) => setField("assignedUser", e.target.value)}
                    onClose={() => setUserSearch("")}
                    MenuProps={{ PaperProps: { style: { maxHeight: 200 } }, autoFocus: false }}
                  >
                    <ListSubheader sx={(theme) => ({ pt: 1, pb: 0.5, backgroundColor: darkMode ? theme.palette.background.card : theme.palette.white.main })}>
                      <TextField
                        size="small"
                        autoFocus
                        placeholder="Search…"
                        fullWidth
                        value={userSearch}
                        onChange={(e) => { e.stopPropagation(); setUserSearch(e.target.value); }}
                        onKeyDown={(e) => e.stopPropagation()}
                        InputProps={{ startAdornment: <InputAdornment position="start"><SearchIcon fontSize="small" /></InputAdornment> }}
                      />
                    </ListSubheader>
                    <MenuItem value=""><em>— Select —</em></MenuItem>
                    {userOpts
                      .filter((o) => o.toLowerCase().includes(userSearch.toLowerCase()))
                      .map((o) => <MenuItem key={o} value={o}>{o}</MenuItem>)}
                  </Select>
                </FormControl>
              </Grid>
            ) : formData.assignedTo === "1" || formData.assignedTo === 1 ? (
              <Grid item xs={6}>
                <FormControl fullWidth sx={{ "& .MuiInputBase-root": { height: 35 } }}>
                  <InputLabel>Assignee Group</InputLabel>
                  <Select
                    value={formData.assignedGroup || ""}
                    label="Assignee Group"
                    onChange={(e) => setField("assignedGroup", e.target.value)}
                    onClose={() => setGroupSearch("")}
                    MenuProps={{ PaperProps: { style: { maxHeight: 200 } }, autoFocus: false }}
                  >
                    <ListSubheader sx={(theme) => ({ pt: 1, pb: 0.5, backgroundColor: darkMode ? theme.palette.background.card : theme.palette.white.main })}>
                      <TextField
                        size="small"
                        autoFocus
                        placeholder="Search…"
                        fullWidth
                        value={groupSearch}
                        onChange={(e) => { e.stopPropagation(); setGroupSearch(e.target.value); }}
                        onKeyDown={(e) => e.stopPropagation()}
                        InputProps={{ startAdornment: <InputAdornment position="start"><SearchIcon fontSize="small" /></InputAdornment> }}
                      />
                    </ListSubheader>
                    <MenuItem value=""><em>— Select —</em></MenuItem>
                    {groupOpts
                      .filter((o) => o.toLowerCase().includes(groupSearch.toLowerCase()))
                      .map((o) => <MenuItem key={o} value={o}>{o}</MenuItem>)}
                  </Select>
                </FormControl>
              </Grid>
            ) : (
              <Grid item xs={6} />
            )}

            {/* ── Row 6: Reviewer + Status (edit only) ── */}
            <Grid item xs={isEditMode ? 6 : 12}>
              <FormControl fullWidth sx={{ "& .MuiInputBase-root": { height: 35 } }}>
                <InputLabel>Reviewer</InputLabel>
                <Select
                  value={formData.reviewer || ""}
                  label="Reviewer"
                  onChange={(e) => setField("reviewer", e.target.value)}
                >
                  <MenuItem value=""><em>— Select —</em></MenuItem>
                  {reviewerOpts.map((o) => <MenuItem key={o} value={o}>{o}</MenuItem>)}
                </Select>
              </FormControl>
            </Grid>
            {isEditMode && (
              <Grid item xs={6}>
                <FormControl fullWidth sx={{ "& .MuiInputBase-root": { height: 35 } }}>
                  <InputLabel>Status</InputLabel>
                  <Select
                    value={formData.currentStats || ""}
                    label="Status"
                    onChange={(e) => setField("currentStats", e.target.value)}
                  >
                    {["New", "Pending", "In Progress", "Pending For Approval", "Completed"].map(
                      (s) => <MenuItem key={s} value={s}>{s}</MenuItem>
                    )}
                  </Select>
                </FormControl>
              </Grid>
            )}

            {/* ── Row 7: Task Description ── */}
            <Grid item xs={12}>
              <TextField
                label="Task Description"
                fullWidth
                multiline
                minRows={2}
                value={formData.taskDescription || ""}
                onChange={(e) => setField("taskDescription", e.target.value)}
              />
            </Grid>

            {/* ── Row 8: Attached File ── */}
            <Grid item xs={12}>
              <Box
                sx={{
                  border: (t) => `1px solid ${alpha(t.palette.divider, 0.7)}`,
                  borderRadius: 1,
                  px: 1.5,
                  py: 1,
                  display: "flex",
                  alignItems: "center",
                  gap: 1.5,
                  minHeight: 55,
                }}
              >
                <MDButton
                  component="label"
                  size="small"
                  variant="outlined"
                  color="info"
                  disabled={fileUploading}
                  sx={{ flexShrink: 0, whiteSpace: "nowrap" }}
                >
                  {fileUploading ? (
                    <Box sx={{ display: "flex", alignItems: "center", gap: 0.5 }}>
                      <CircularProgress size={12} color="inherit" />
                      Uploading…
                    </Box>
                  ) : "Choose File"}
                  <input
                    type="file"
                    hidden
                    accept=".mp3,.wav,.pdf,.txt,.csv,.jpg,.jpeg,.png"
                    onChange={(e) => handleFileUpload(e.target.files?.[0])}
                  />
                </MDButton>
                <MDTypography sx={{ fontSize: "0.72rem", color: localFileName ? "text" : "text", overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap", flex: 1 }}>
                  {localFileName || "No file chosen (.mp3 .wav .pdf .txt .csv .jpg .jpeg .png)"}
                </MDTypography>
                {localFileName && (
                  <Tooltip title="Remove file">
                    <IconButton size="small" sx={{ p: 0.25, color: "error.main" }} onClick={() => { setField("filePath", ""); setLocalFileName(""); }}>
                      <CloseIcon sx={{ fontSize: 14 }} />
                    </IconButton>
                  </Tooltip>
                )}
              </Box>
            </Grid>

          </Grid>
        </DialogContent>

        <DialogActions sx={{ px: 2, pb: 1.5, gap: 1 }}>
          <MDButton
            size="small"
            variant="outlined"
            color="secondary"
            onClick={() => setOpenForm(false)}
            disabled={saving}
          >
            Cancel
          </MDButton>
          <MDButton
            size="small"
            variant="gradient"
            color="success"
            onClick={handleSave}
            disabled={saving}
          >
            {saving ? (
              <Box sx={{ display: "flex", alignItems: "center", gap: 0.5 }}>
                <CircularProgress size={13} color="inherit" />
                Saving…
              </Box>
            ) : (
              <Box sx={{ display: "flex", alignItems: "center", gap: 0.5 }}>
                <SaveOutlinedIcon sx={{ fontSize: 14 }} />
                {isEditMode ? "Update" : "Create"}
              </Box>
            )}
          </MDButton>
        </DialogActions>
      </Dialog>

      {/* ── Task Detail Dialog ── */}
      <TaskDetailDialog
        open={viewTask !== null}
        task={viewTask}
        onClose={() => setViewTask(null)}
        onEdit={(task) => { setViewTask(null); openEditForm(task); }}
        darkMode={darkMode}
      />

      {/* ── Delete Confirmation Dialog ── */}
      <Dialog
        open={confirmDeleteId !== null}
        onClose={() => setConfirmDeleteId(null)}
        maxWidth="xs"
        fullWidth
        PaperProps={{ sx: { borderRadius: 2 } }}
      >
        <DialogTitle sx={{ fontWeight: 700, fontSize: "0.95rem", color: "error.main" }}>
          Delete Task
        </DialogTitle>
        <DialogContent>
          <MDTypography sx={{ fontSize: "0.85rem" }}>
            Are you sure you want to delete this task? This action cannot be undone.
          </MDTypography>
        </DialogContent>
        <DialogActions sx={{ px: 2, pb: 1.5, gap: 1 }}>
          <MDButton
            size="small"
            variant="outlined"
            color="secondary"
            onClick={() => setConfirmDeleteId(null)}
          >
            Cancel
          </MDButton>
          <MDButton
            size="small"
            variant="gradient"
            color="error"
            onClick={() => handleDelete(confirmDeleteId)}
          >
            Delete
          </MDButton>
        </DialogActions>
      </Dialog>

      <CTLNotification notify={notify} setNotify={setNotify} />
    </MDBox>
  );
}
